import React, { useRef, useEffect, useState, useCallback } from "react";
import { Platform, View, StyleSheet } from "react-native";
import { useForm, Controller } from "react-hook-form";
import { RichEditor, RichToolbar, actions } from "react-native-pell-rich-editor";
import { DatePickerInput } from "react-native-paper-dates";

import CustomText from "../../../common/general/CustomText/CustomText";
import CustomTextInput from "../../../common/general/CustomTextInput/CustomTextInput";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import { File, Remove } from "../../../svgs/CustomCoursework";
import FormLayout from "../common/FormLayout";
import { StoragePathType, ActivityState } from "../../../../graphql/generated/graphql";
import { VIMEO_REGEX, YOUTUBE_REGEX } from "../../../../utils/regex.dictionary";
import TextEditor from "../../../TextEditor/TextEditor";
import FileUploadComponent from "../../../file/FileUploadComponent/FileUploadComponent";
import { ProcessedFile } from "../../../../hooks/useFileUpload";

const ResourceForm = ({ activityData, onSave, onCancel, error, isSubmitting }) => {
  // Initialize react-hook-form
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      activityName: activityData?.title || "",
      description: activityData?.description || "",
      videoUrl: activityData?.videoUrl || "",
      files: activityData?.files || [],
      activityState: activityData?.activityState || "draft",
      scheduledDate: activityData?.scheduledDate || undefined,
    },
  });
  const [uploadError, setUploadError] = useState(null);

  const editorRef = useRef(null);
  const watchActivityName = watch("activityName");
  const watchDescription = watch("description");
  const watchFiles = watch("files");
  const watchActivityState = watch("activityState");

  // Temporarily disable image upload until we resolve issues
  // const { handleImageUpload } = useEditorImageUpload(editorRef);

  // const onPressAddImage = useCallback(async () => {
  //   const result = await handleImageUpload();
  //   if (result?.fileURL) {
  //     return result.fileURL;
  //   }
  // }, [handleImageUpload]);
  const onPressAddImage = undefined;

  const isWeb = Platform.OS === "web";

  // Initialize the editor with the description content
  useEffect(() => {
    if (editorRef.current && activityData?.description) {
      // Set the initial content once when the component mounts
      editorRef.current.setContentHTML(activityData.description);
    }
  }, [activityData]);

  // File upload handling
  const handleAfterUpload = async (processedFilesArray: ProcessedFile[]) => {
    setUploadError(null);
    const newFiles = [
      ...watchFiles,
      ...processedFilesArray.map((processedFile) => ({
        fileId: processedFile.file.id,
        fileName: processedFile.file.fileName,
        fileUrl: processedFile.file.fileURL,
        contentType: processedFile.mimeType,
        size: processedFile.size,
      })),
    ];
    setValue("files", newFiles);
  };

  // Handle removing a file
  const handleFileRemove = (fileToRemove) => {
    const updatedFiles = watchFiles.filter((file) => file !== fileToRemove);
    setValue("files", updatedFiles);
  };

  // Render file list
  const renderFileList = () => {
    return watchFiles.map((file, index) => (
      <View key={index} style={styles.uploadedFile}>
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <File />
          <CustomSpacing type="horizontal" size="s" />
          <CustomText size="m" weight="ultraBold" text={truncateText(file.fileName, 40)} useTranslationText={false} />
        </View>

        <CustomButton
          leftIcon={<Remove style={{ paddingRight: 4 }} />}
          text="Remove"
          size="s"
          style={{ backgroundColor: "#D0D5DD", borderRadius: 4 }}
          textStyle={{ fontSize: 14, fontWeight: 700 }}
          onPress={() => handleFileRemove(file)}
        />
      </View>
    ));
  };

  // Helper function to truncate long text
  const truncateText = (text, maxLength) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Form submission
  const onSubmit = (data) => {
    if (!data.activityName.trim()) {
      return;
    }

    // Create the activity data object with all required fields
    const activityData = {
      title: data.activityName,
      description: data.description,
      videoUrl: data.videoUrl || "",
      files: data.files || [],
      type: "Resource",
      activityState: data.activityState,
      scheduledDate: data.scheduledDate,
    };

    onSave(activityData);
  };

  return (
    <FormLayout
      title={`${activityData?.id ? "Edit" : "Create"} Resource`}
      isSubmitting={isSubmitting}
      onCancel={onCancel}
      onSubmit={handleSubmit(onSubmit)}
      actionText={activityData?.id ? "Save" : "Create"}
      disableSubmit={!watchActivityName}
      error={error}>
      <CustomText text="Resource Name" size="m" weight="ultraBold" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="activityName"
        control={control}
        rules={{ required: true }}
        render={({ field: { onChange, onBlur, value } }) => (
          <CustomTextInput placeholder="Enter resource name" onBlur={onBlur} onChangeText={onChange} value={value} />
        )}
      />
      {errors.activityName && <CustomText text="Activity name is required" style={styles.errorText} />}

      <CustomSpacing type="vertical" size="xl" />
      <CustomText text="Description" size="m" weight="ultraBold" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="description"
        control={control}
        rules={{ required: false }}
        render={({ field: { onChange, value } }) =>
          isWeb ? (
            <TextEditor
              value={value}
              onChange={(content) => {
                onChange(content);
              }}
              disabled={isSubmitting}
              placeholder="Enter resource description"
              onPressAddImage={onPressAddImage}
            />
          ) : (
            <>
              <RichEditor
                style={styles.editor}
                ref={editorRef}
                initialContentHTML={value}
                onChange={(content) => {
                  onChange(content);
                }}
                placeholder="Enter resource description"
              />
              <RichToolbar
                editor={editorRef}
                actions={[
                  actions.setBold,
                  actions.setItalic,
                  actions.setUnderline,
                  actions.heading1,
                  actions.insertLink,
                  actions.insertImage,
                ]}
                style={styles.toolbar}
                onPressAddImage={onPressAddImage}
              />
            </>
          )
        }
      />

      <CustomSpacing type="vertical" size="xl" />
      <CustomText text="File Upload" size="m" weight="ultraBold" />
      <CustomText style={styles.subText} text="Upload resource documents for students to access" />
      <CustomSpacing type="vertical" size="s" />
      <FileUploadComponent
        storagePath={StoragePathType.Resources}
        afterUpload={handleAfterUpload}
        onError={(msg) => setUploadError(msg)}
      />
      {uploadError && <CustomText useTranslationText={false} text={uploadError} style={styles.errorText} />}
      {renderFileList()}

      <CustomSpacing type="vertical" size="xl" />
      <CustomText text="Video URL" size="m" weight="ultraBold" />
      <CustomText style={styles.subText} text="Paste either a YouTube or Vimeo link here" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="videoUrl"
        control={control}
        rules={{
          validate: (value) => {
            if (!value) return true;
            const isYouTube = YOUTUBE_REGEX.test(value.trim());
            const isVimeo = VIMEO_REGEX.test(value.trim());
            return isYouTube || isVimeo || "Please enter a valid YouTube or Vimeo link.";
          },
        }}
        render={({ field: { onChange, onBlur, value } }) => (
          <CustomTextInput
            placeholder="Paste either a YouTube or Vimeo link here"
            onBlur={onBlur}
            onChangeText={onChange}
            value={value}
          />
        )}
      />
      <CustomSpacing type="vertical" size="m" />
      <View style={styles.activityState}>
        <CustomText text="Publish this Activity" size="l" weight="ultraBold" useTranslationText={false} />
        <CustomText
          text="Publish this activity now or schedule a publish date."
          size="s"
          useTranslationText={false}
          style={styles.stepDescription}
        />
        <Controller
          control={control}
          name="activityState"
          render={({ field: { onChange, value } }) => (
            <div style={{ display: "flex", gap: 10, marginTop: 5 }}>
              {Object.keys(ActivityState).map((state) => (
                <CustomButton
                  key={state}
                  text={ActivityState[state]}
                  size="s"
                  style={[styles.activityStateOption, ActivityState[state] === value && styles.activeState]}
                  onPress={() => {
                    onChange(ActivityState[state]);
                  }}
                />
              ))}
            </div>
          )}
        />
        {watchActivityState === ActivityState.Scheduled && (
          <>
            <CustomText
              text="This Activity will automatically be published after:"
              size="s"
              useTranslationText={false}
              style={styles.stepDescription}
            />
            <Controller
              control={control}
              name="scheduledDate"
              render={({ field: { onChange, value } }) => (
                <div style={{ maxWidth: 250, marginTop: 10 }}>
                  <DatePickerInput
                    locale="en"
                    value={value instanceof Date ? value : value ? new Date(value) : undefined}
                    onChange={onChange}
                    inputMode="start"
                    mode="outlined"
                    outlineColor={"#D1D5DB"}
                    selectionColor={undefined}
                    activeOutlineColor={"blue"}
                    withDateFormatInLabel={false}
                    placeholder="MM/DD/YYYY"
                    theme={{
                      colors: {
                        primary: "#000000",
                        onSurface: "#667085",
                      },
                      roundness: 8,
                    }}
                    style={styles.datePicker}
                    aria-label="Schedule published date picker"
                  />
                </div>
              )}
            />
          </>
        )}
      </View>
      {errors.videoUrl && (
        <CustomText useTranslationText={false} text={errors.videoUrl.message} style={styles.errorText} />
      )}
    </FormLayout>
  );
};

const styles = StyleSheet.create({
  editor: {
    minHeight: 200,
    borderColor: "#ccc",
    borderWidth: 1,
    borderRadius: 4,
  },
  toolbar: {
    backgroundColor: "#f1f1f1",
    borderColor: "#ccc",
    borderTopWidth: 1,
  },
  subText: {
    color: "#667085",
    fontSize: 14,
    fontWeight: "400",
  },
  errorText: {
    color: "#FF0000",
    marginTop: 8,
  },
  uploadedFile: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 12,
    backgroundColor: "#F3F4F6",
    borderRadius: 8,
    marginBottom: 16,
    marginTop: 16,
  },
  datePicker: {
    backgroundColor: "#FFFFFF",
    borderColor: "#D5D7DA",
    borderRadius: 10,
    color: "#000000",
    fontSize: 14,
  },
  activityState: {
    marginBottom: 20,
    padding: 15,
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
  },
  activeState: {
    backgroundColor: "#0084FF1A",
    color: "white",
    borderColor: "#0084FF",
  },
  activityStateOption: {
    borderRadius: 30,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderWidth: 2,
    borderStyle: "solid",
    color: "#667085",
    borderColor: "transparent",
  },
  stepDescription: {
    color: "#667085",
    marginTop: 5,
  },
});

export default ResourceForm;
